/**
 * Singleton reactive state for floating damage numbers in the combat scene.
 * useCombatLoop calls `showDamage()` on each tick; CombatScene.vue renders them.
 */

export interface DamageNumber {
  id: number
  amount: number
  formatted: string
  x: number
  y: number
  isCrit: boolean
  isHeal: boolean
  type: 'damage' | 'gold' | 'xp'
}

let nextId = 0

const numbers = ref<DamageNumber[]>([])
const removeTimers = new Map<number, ReturnType<typeof setTimeout>>()
const MAX_VISIBLE_NUMBERS = 24

/** Même format que les récompenses flottantes en combat (K / M / B) — à réutiliser pour l’UI « preview ». */
export function formatCombatRewardNumber(amount: number): string {
  if (amount >= 1_000_000_000) return `${(amount / 1_000_000_000).toFixed(1)}B`
  if (amount >= 1_000_000) return `${(amount / 1_000_000).toFixed(1)}M`
  if (amount >= 1_000) return `${(amount / 1_000).toFixed(1)}K`
  return String(amount)
}

function cancelRemoveTimer(id: number) {
  const handle = removeTimers.get(id)
  if (handle !== undefined) {
    clearTimeout(handle)
    removeTimers.delete(id)
  }
}

function scheduleRemoval(id: number, delayMs: number) {
  cancelRemoveTimer(id)
  removeTimers.set(
    id,
    setTimeout(() => {
      removeTimers.delete(id)
      const idx = numbers.value.findIndex((n) => n.id === id)
      if (idx !== -1) numbers.value.splice(idx, 1)
    }, delayMs),
  )
}

function pushNumber(num: DamageNumber, lifetimeMs: number) {
  numbers.value.push(num)
  while (numbers.value.length > MAX_VISIBLE_NUMBERS) {
    const dropped = numbers.value.shift()
    if (dropped) cancelRemoveTimer(dropped.id)
  }
  scheduleRemoval(num.id, lifetimeMs)
}

export function useDamageNumbers() {
  function showDamage(amount: number, options?: { isCrit?: boolean; isHeal?: boolean; x?: number; y?: number }) {
    if (amount <= 0) return

    // Slight random offset so numbers don't stack exactly
    const baseX = options?.x ?? 50
    const baseY = options?.y ?? 50
    const jitterX = baseX + (Math.random() - 0.5) * 30
    const jitterY = baseY + (Math.random() - 0.5) * 15

    const num: DamageNumber = {
      id: nextId++,
      amount,
      formatted: formatCombatRewardNumber(amount),
      x: jitterX,
      y: jitterY,
      isCrit: options?.isCrit ?? false,
      isHeal: options?.isHeal ?? false,
      type: 'damage',
    }

    pushNumber(num, 1400)
  }

  function showReward(amount: number, rewardType: 'gold' | 'xp', options?: { x?: number; y?: number }) {
    if (amount <= 0) return
    const baseX = options?.x ?? 50
    const baseY = options?.y ?? 50
    const num: DamageNumber = {
      id: nextId++,
      amount,
      formatted: (rewardType === 'gold' ? '💰 +' : '✦ +') + formatCombatRewardNumber(amount),
      x: baseX + (Math.random() - 0.5) * 18,
      y: baseY + (Math.random() - 0.5) * 10,
      isCrit: false,
      isHeal: false,
      type: rewardType,
    }
    pushNumber(num, 1600)
  }

  function clearAll() {
    for (const handle of removeTimers.values()) clearTimeout(handle)
    removeTimers.clear()
    numbers.value = []
  }

  return { numbers: readonly(numbers), showDamage, showReward, clearAll }
}
